import type {
  NoticeCreateRequest,
  NoticeItemResponse,
  NoticeListRequestQuery,
  NoticeUpdateRequest
} from '@lmc/ts-common'
import { getJSON, postJSON } from 'common/services/api'
import qs from 'qs'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { NoticeFormValues } from '../components/NoticeForm'
import type { NoticeboardSortObject } from '../components/NoticesTable'

export const GET_NOTICES = `/api/list/notices`

export enum NOTICES_FETCH_TYPE {
  SINGLE = 'SINGLE',
  LIST = 'MULTIPLE'
}

type CommonProps<Type extends NOTICES_FETCH_TYPE> = {
  onCreateSuccess?: () => void
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  page?: number
  fetch: Type
  sortParams?: NoticeboardSortObject
}

type UseNoticesProps<Type extends NOTICES_FETCH_TYPE> = CommonProps<Type> &
  (Type extends NOTICES_FETCH_TYPE.SINGLE
    ? {
        noticeID: string | undefined // Required when Type is 'SINGLE'
        title?: undefined
      }
    : {
        noticeID?: undefined // Optional when Type is 'MULTIPLE'
        title?: string
      })

type CommonReturn = {
  isLoading: boolean
  isNoticeLoading: boolean
  createNotice: (values: NoticeFormValues) => Promise<NoticeItemResponse>
  deleteNotice: () => Promise<unknown>
  updateNotice: (values: NoticeFormValues) => Promise<NoticeItemResponse>
  refetch: () => void
  count?: number
}

export type UseNoticesReturnOne = CommonReturn & {
  result: NoticeItemResponse | undefined
}

export type UseNoticesReturnMany = CommonReturn & {
  result: NoticeItemResponse[] | undefined
}

type NoticesData<T> = { result: T[]; count?: number }

const useNotices = <Type extends NOTICES_FETCH_TYPE>({
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
  fetch,
  noticeID,
  title,
  page,
  sortParams
}: UseNoticesProps<Type>): UseNoticesReturnOne | UseNoticesReturnMany => {
  const listQueryParams = qs.stringify({
    title,
    pagination: {
      page: page || 1,
      sort: sortParams
    }
  } as NoticeListRequestQuery)

  const {
    isLoading: areNoticesLoading,
    data: noticesData,
    refetch: refetchNotices
  } = useQuery<NoticesData<NoticeItemResponse>>(
    ['notices', title, page, sortParams],
    () => getJSON({ url: `${GET_NOTICES}?${listQueryParams}` }),
    {
      enabled: fetch === NOTICES_FETCH_TYPE.LIST
    }
  )
  const {
    data: noticeData,
    isLoading: isNoticeLoading,
    refetch: refetchNotice
  } = useQuery<{ result: NoticeItemResponse }>(
    ['notice', noticeID],
    () => getJSON({ url: `${GET_NOTICES}/${noticeID}` }),
    {
      enabled: fetch === NOTICES_FETCH_TYPE.SINGLE || noticeID !== undefined,
      onSuccess: () => {
        queryClient.invalidateQueries('notices')
      }
    }
  )

  const createFunction = (values: NoticeCreateRequest) =>
    postJSON({
      url: `list/notices`,
      method: 'POST',
      json: values
    })

  const updateFunction = (values: NoticeUpdateRequest) =>
    postJSON({
      url: `list/notices/${noticeID}`,
      method: 'PATCH',
      json: values
    })

  const deleteFunction = () =>
    postJSON({
      url: `list/notices/${noticeID}`,
      method: 'DELETE'
    })

  const queryClient = useQueryClient()

  const { mutateAsync: createMutation } = useMutation<
    NoticeItemResponse,
    unknown,
    NoticeCreateRequest
  >(['create-notice'], createFunction, {
    onSuccess: () => {
      onCreateSuccess?.()
      refetchHandler()
    }
  })

  const { mutateAsync: updateMutation } = useMutation<
    NoticeItemResponse,
    unknown,
    NoticeUpdateRequest
  >([`update-notice-${noticeID}`], updateFunction, {
    onSuccess: () => {
      onUpdateSuccess?.()
      refetchHandler()
    }
  })

  const { mutateAsync: deleteMutation } = useMutation(
    [`delete-notice`, noticeID],
    deleteFunction,
    {
      onSuccess: () => {
        onDeleteSuccess?.()
        refetchNotices()
      }
    }
  )

  const refetchHandler = () => {
    switch (fetch) {
      case NOTICES_FETCH_TYPE.LIST:
        queryClient.invalidateQueries('notices')
        refetchNotices()
        break
      default:
        queryClient.invalidateQueries('notice')
        refetchNotice()
    }
  }

  if (fetch === NOTICES_FETCH_TYPE.SINGLE) {
    return {
      isNoticeLoading,
      isLoading: areNoticesLoading,
      result: noticeData?.result,
      createNotice: createMutation,
      deleteNotice: deleteMutation,
      updateNotice: updateMutation,
      refetch: refetchHandler
    }
  }
  return {
    isNoticeLoading,
    isLoading: areNoticesLoading,
    result: noticesData?.result,
    count: noticesData?.count,
    createNotice: createMutation,
    deleteNotice: deleteMutation,
    updateNotice: updateMutation,
    refetch: refetchHandler
  }
}

export default useNotices
